// Range slider controller — the invariant of a two-handle range.
//
// Ui::RangeSliderComponent stacks two native `input[type=range]` handles over
// one rail. Native inputs buy keyboard support, Home/End, PageUp/PageDown and
// the OS touch behaviour for free; what they do NOT know about is each other, so
// this controller owns exactly three things:
//
//   1. `lower <= upper`, always. The handle being dragged pushes the OTHER one
//      rather than being clamped itself: dragging the warning threshold past the
//      alert threshold means "the alert goes up too", and silently refusing the
//      drag is how a dual slider ends up feeling broken.
//   2. The band between the handles (`--range-fill-start` / `--range-fill-end`
//      on the fill element, whose geometry lives in range_slider.css), and — when
//      the host supplied `band_colours` — the three rail bands themselves.
//   3. The two live readouts.
//
// `unit` is a value, not a hardcoded suffix: the gem ships no copy, so every
// string is the host's.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["lower", "upper", "fill", "rail", "lowerValue", "upperValue"]

  static values = {
    min: { type: Number, default: 0 },
    max: { type: Number, default: 100 },
    unit: { type: String, default: "" },
    // The host's stored unit against the rail's own scale, so the live readout
    // can print what the server printed. A handle's position is a share of
    // `min..max`, and a RackEye score of 145 sits at 14.5% of a 0..1000 rail —
    // so the readout is the POSITION (14.5), not the stored number. Default 1
    // reproduces the component's own default exactly.
    displayDivisor: { type: Number, default: 1 },
    bandColours: { type: Array, default: [] },
  }

  connect() {
    this._settle()
    this._paint()
  }

  onLowerInput() {
    const lower = this._clamp(this.lowerTarget.value)
    if (lower > this._upper()) this.upperTarget.value = String(lower)
    this._paint()
  }

  onUpperInput() {
    const upper = this._clamp(this.upperTarget.value)
    if (upper < this._lower()) this.lowerTarget.value = String(upper)
    this._paint()
  }

  // Crossed values can only arrive from the server — the markup is the host's
  // data, and nothing else writes these inputs. Settling them onto the lower
  // handle's value puts both thumbs on one spot, where the operator can see the
  // problem and drag either one: the visible recovery.
  _settle() {
    if (this._lower() > this._upper())
      this.upperTarget.value = String(this._lower())
  }

  _paint() {
    const lower = this._lower()
    const upper = this._upper()

    if (this.hasFillTarget) {
      this.fillTarget.style.setProperty("--range-fill-start", this._percent(lower))
      this.fillTarget.style.setProperty("--range-fill-end", this._percent(upper))
    }
    if (this.hasLowerValueTarget)
      this.lowerValueTarget.textContent = this._display(lower)
    if (this.hasUpperValueTarget)
      this.upperValueTarget.textContent = this._display(upper)

    this._paintBands(lower, upper)
  }

  // The three rail bands, cut where the handles are — and recut on every input.
  //
  // A gradient whose stops are percentages fixed at render time cannot survive a
  // drag: move the warning handle and the colour boundary stays behind, so the
  // rail ends up contradicting the handle that defines it. That is the one
  // reading this control must never get wrong, so the positions live here and
  // the host only supplies the three colours.
  //
  // Each colour is written TWICE, at both edges of its band: consecutive stops
  // at the same position are a hard cut, which is what a band is. Written once
  // they would blend into a continuous ramp — grey sliding into amber across the
  // whole left half — and "which band am I in" would be unanswerable again.
  _paintBands(lower, upper) {
    const colours = this.bandColoursValue
    if (!colours || colours.length < 3) return

    const [below, between, above] = colours
    const cut = this._percent(lower)
    const cut2 = this._percent(upper)
    const stops = [
      `${below} 0%`,
      `${below} ${cut}`,
      `${between} ${cut}`,
      `${between} ${cut2}`,
      `${above} ${cut2}`,
      `${above} 100%`,
    ]

    if (this.hasRailTarget)
      this.railTarget.style.setProperty(
        "--range-band-gradient",
        `linear-gradient(to right, ${stops.join(", ")})`,
      )
    // The fill spans exactly the middle band, so it wears that band's colour:
    // the stylesheet's default neutral would paint an opaque block straight over
    // the amber one.
    if (this.hasFillTarget) this.fillTarget.style.setProperty("--range-band-colour", between)
  }

  _lower() {
    return this._clamp(this.lowerTarget.value)
  }

  _upper() {
    return this._clamp(this.upperTarget.value)
  }

  // An empty or unparsable value reads as the floor of the scale. Never let a
  // NaN reach the DOM: it would silently blank the readout instead.
  _clamp(value) {
    const number = Number.parseFloat(String(value))
    if (Number.isNaN(number)) return this.minValue

    return Math.min(Math.max(number, this.minValue), this.maxValue)
  }

  _percent(value) {
    const span = this.maxValue - this.minValue
    if (!(span > 0)) return "0%"

    const share = Math.min(Math.max(((value - this.minValue) * 100) / span, 0), 100)

    return `${Math.round(share * 100) / 100}%`
  }

  // The same reading the server rendered — see `display_value` on the
  // component. Only the READOUT is divided: the handle's own `value` stays the
  // stored number, so a drag keeps submitting exactly what the form was given.
  _display(value) {
    const divisor = this.displayDivisorValue
    const scaled = divisor && divisor !== 1 ? value / divisor : value

    return `${this._numberString(scaled)}${this.unitValue}`
  }

  // 14.5 reads as "14.5", 100.0 as "100" — a trailing .0 on a threshold reads
  // as false precision. Mirrors the component's `number_string`.
  _numberString(value) {
    return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(4)))
  }
}
